import * as React from 'react';
import { cn } from '@relay-towing/tokens';
import {
  diffLineRecipe,
  diffSymbolRecipe,
  type DiffSymbolRecipeProps,
} from '../recipes/diff-line.recipe';
import { Inline } from './Layout';
import { Text } from './Text';

export interface DiffLineProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  /** What happened: the value was added, removed, or changed. */
  kind: NonNullable<DiffSymbolRecipeProps['kind']>;
  /** The field's name. Omit for a line that is one phrase — "2 Images". */
  field?: React.ReactNode;
  /** The value; for a change, the value it became. */
  value?: React.ReactNode;
  /** For a change: the value it was. Drawn struck through, before an arrow. */
  from?: React.ReactNode;
}

/**
 * One line of an audit trail.
 *
 *   <DiffLine kind="add" field="Licence plate" value="ABC 123" />
 *   <DiffLine kind="change" field="Odometer" from="41,200" value="41,350" />
 *   <DiffLine kind="remove" value="1 Image" />
 *
 * The call activity log lists what each event changed, and drew every line
 * by hand: a coloured symbol, the field in medium, the old value struck
 * through, an arrow, the new value. Three shapes, each with its own spans.
 * The symbol is what the kind changes; everything else is a Text role.
 */
export const DiffLine = React.forwardRef<HTMLDivElement, DiffLineProps>(
  function DiffLine({ kind, field, value, from, className, ...props }, ref) {
    const symbol = kind === 'add' ? '+' : kind === 'remove' ? '−' : '~';
    const changed = kind === 'change' && from !== undefined;
    return (
      <Inline
        ref={ref}
        gap={2}
        align="baseline"
        wrap
        className={cn(diffLineRecipe(), className)}
        {...props}
      >
        <Text
          as="span"
          variant="inherit"
          className={diffSymbolRecipe({ kind })}
        >
          {symbol}
        </Text>
        {field !== undefined ? (
          <Text as="span" variant="body-sm-strong">
            {field}:
          </Text>
        ) : null}
        {changed ? (
          <>
            <Text
              as="span"
              variant="body-sm"
              tone="muted"
              className="line-through"
            >
              {from}
            </Text>
            <Text as="span" variant="caption" tone="info">
              {'→'}
            </Text>
            <Text as="span" variant="body-sm-strong">
              {value}
            </Text>
          </>
        ) : (
          <Text as="span" variant="body-sm" tone="muted">
            {value}
          </Text>
        )}
      </Inline>
    );
  }
);
